import { BranchHashCell } from "@/components/table/branchHash";
import { TrainingSessionRefLink } from "./trainingRefs";
import { formatPercentStat } from "./compare/evalCompareSummaryModel";
import type { BaselineSeries, EvalBaselineState } from "./evalBaselineModel";
import { configLine, type EvalResultSeries } from "./compare/evalCompareModel";
import { Spinner } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import type { TrainingEvalTermView } from "@/api/trainingEval";
import { runDurationSeconds } from "@/modules/evaluation/public";
import { CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";
import { EmptyState, noticeChipClassName } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName, LoadingState } from "@/shared/ui/loadingState/public";
import { BoxPlotChart, boxPlotGridClass } from "./compare/BoxPlotChart";
import { KpiCompareChart } from "./compare/KpiCompareChart";
import {
  EvalRunDetailNotice,
  EvalRunDetailSection,
  EvalRunDetailSummaryStat,
} from "./EvalRunDetailElements";
import type { BoxStats } from "./evalDistributionModel";
import {
  formatDetailDuration,
  formatDetailNumber,
  metricValue,
  outcomeBreakdown,
  SUCCESS_RATE_METRIC_KEYS,
  termBoxStats,
} from "./evalRunResultModel";
import type { EvalRunDetailController } from "./useEvalRunDetailController";
import { EpisodeTimesButton } from "./EpisodeTimesModal";
import { ISAAC_FINAL_NOTE, isaacSuccessRates, matchingIsaacCounts } from "./isaacEvalModel";
import styles from "./EvalRunDetailView.module.css";

// 상세 화면이 열린 run 을 비교 표·차트에 표시할 때 쓰는 시리즈다. 이름과 색을 두 벌 두면
// 같은 화면의 표와 차트가 "이 실행" 을 서로 다른 색으로 표시한다.
export const DETAIL_RUN_SERIES: EvalResultSeries = {
  key: "run",
  label: "이 실행",
  shortLabel: "이 실행",
  sublabel: "",
  color: CHART_SERIES_PALETTE[0],
  summary: {
    finalSuccessRate: null,
    durationSec: null,
    outcomeBreakdown: [],
    distributions: [],
  },
};

// 사용자는 성공률 차이를 값보다 먼저 읽는다. 높을수록 좋은 지표라서 위쪽이 성공 색이다.
function SuccessRateDelta({
  runRate,
  baselineRate,
  fallbackConfig,
}: {
  runRate: number | null;
  baselineRate: number | null;
  fallbackConfig: string | null;
}): ReactNode {
  if (runRate === null || baselineRate === null) return null;
  // 대신 조회한 config 의 baseline 과 비교한 값이면 차이 바로 옆에 그 config 를 밝힌다.
  const versus =
    fallbackConfig === null ? null : (
      <span className={styles.muted}>{`${configLine(fallbackConfig)} 대비`}</span>
    );
  const delta = (runRate - baselineRate) * 100;
  if (Math.abs(delta) < 0.05)
    return (
      <>
        <span className={styles.muted}>Baseline과 같음</span>
        {versus}
      </>
    );
  const better = delta > 0;
  return (
    <>
      <span className={better ? styles.deltaUp : styles.deltaDown}>
        {/* 소수 자리를 첫째 자리로 고정한다. 값마다 자리수가 다르면 두 run 을 눈으로 비교하기 어렵다. */}
        {`${better ? "▲" : "▼"} ${Math.abs(delta).toFixed(1)}%p`}
      </span>
      {versus}
    </>
  );
}

// 성공률과 소요 시간은 사용자가 run 을 열자마자 읽는 값이다. 그래서 머리글 바로 아래에 둔다.
export function EvalRunHeadlineStats({
  summary,
  metricsState,
  baselineSeries,
}: {
  summary: EvalRunDetailController["summary"];
  metricsState: EvalRunDetailController["metricsState"];
  baselineSeries: BaselineSeries | null;
}): ReactNode {
  const successRate = metricValue(
    metricsState.status === "success" ? metricsState.data : null,
    SUCCESS_RATE_METRIC_KEYS,
  );
  const metrics = metricsState.status === "success" ? metricsState.data : null;
  const counts = matchingIsaacCounts(
    successRate,
    metricValue(metrics, ["total_true_success"]) ?? NaN,
    metricValue(metrics, ["total_episodes"]) ?? NaN,
  );
  const hasIsaacConditions = isaacSuccessRates(metrics?.metrics_all ?? {}).some(
    (metric) => metric.value !== null,
  );
  // 두 policy 가 어떤 config 로 학습됐는지 같은 자리(값 줄 바로 아래)에 보인다.
  const runConfig = configLine(summary.config_name);
  const baselineConfig = baselineSeries === null ? null : configLine(baselineSeries.config);
  return (
    <div className={styles.headlineStats}>
      {/* 성공률은 baseline 과 한 카드에서 읽는다. 두 카드로 나누면 사용자가 두 카드를 오가며 비교해야 한다. */}
      <div className={`${styles.stat} ${styles.finalSuccess}`}>
        <span className={styles.muted}>최종 성공률</span>
        <div className={styles.baselineRow}>
          {/* 대기·조회 실패는 값이 없는 실행의 미측정 상태와 구분한다. */}
          {metricsState.status === "error" ? (
            <span className={noticeChipClassName}>조회 실패</span>
          ) : metricsState.status !== "success" ? (
            <span className={styles.pendingLine}>
              <Spinner size="sm" className={inlineSpinnerClassName} />
              불러오는 중
            </span>
          ) : (
            <strong>{successRate === null ? "미측정" : formatPercentStat(successRate)}</strong>
          )}
          <SuccessRateDelta
            runRate={successRate}
            baselineRate={baselineSeries?.summary.finalSuccessRate ?? null}
            fallbackConfig={baselineSeries?.baseline.fallbackConfig ?? null}
          />
        </div>
        {counts !== null ? (
          <span className={styles.muted}>
            성공 {counts.satisfied} / 전체 {counts.measured} Episode
          </span>
        ) : null}
        <span className={styles.muted}>
          {hasIsaacConditions
            ? ISAAC_FINAL_NOTE
            : "Evaluation에서 최종 성공으로 판정된 Episode의 비율입니다."}
        </span>
        {runConfig !== null ? <span className={styles.muted}>{runConfig}</span> : null}
        {/* baseline 값과 그 출처를 한 블록에 묶는다. 구분선이 없으면 hash·Seed 가 이 실행의
            값으로 읽힌다. */}
        {baselineSeries !== null ? (
          <div className={styles.baselineStat}>
            <div className={styles.baselineRow}>
              {/* 비교 창의 열 머리처럼 "Baseline" 이 schedule session 창을 연다. */}
              <TrainingSessionRefLink
                sessionId={baselineSeries.baseline.sessionId}
                displayName="Baseline"
                sessionName={baselineSeries.baseline.sessionName ?? undefined}
              />
              <strong>
                {baselineSeries.summary.finalSuccessRate === null
                  ? "N/A"
                  : formatPercentStat(baselineSeries.summary.finalSuccessRate)}
              </strong>
            </div>
            {baselineConfig !== null ? (
              <span className={styles.muted}>{baselineConfig}</span>
            ) : null}
            <div className={styles.baselineRow}>
              <BranchHashCell branch="main" hash={baselineSeries.baseline.hash} />
              <span className={styles.muted}>{baselineSeries.sublabel}</span>
            </div>
            {baselineSeries.baseline.sessionName !== null ? (
              <span className={styles.muted}>{baselineSeries.baseline.sessionName}</span>
            ) : null}
          </div>
        ) : null}
      </div>
      <EvalRunDetailSummaryStat
        label="실행 소요 시간"
        value={formatDetailDuration(runDurationSeconds(summary))}
      />
    </div>
  );
}

export function EvalRunResultSection({
  runId,
  metricsState,
  termsState,
  baseline,
  onRetry,
}: {
  runId: string;
  baseline: EvalBaselineState;
  metricsState: EvalRunDetailController["metricsState"];
  termsState: EvalRunDetailController["termsState"];
  onRetry: () => void;
}): ReactNode {
  const metrics = metricsState.status === "success" ? metricsState.data : null;
  const terms = termsState.status === "success" ? termsState.data : null;
  const successRate = metricValue(metrics, SUCCESS_RATE_METRIC_KEYS);
  const p95 = metricValue(metrics, ["latency_p95", "inference_time_ms_p95", "p95_latency_ms"]);
  const failuresAfterReset = metricValue(metrics, ["total_failure_after_reset"]);
  const outcomes = outcomeBreakdown(terms);
  const baselineSeries = baseline.series;
  const failure = resultLoadFailure(metricsState, termsState);
  const pending = [metricsState, termsState].some(
    (state) => state.status === "loading" || state.status === "idle",
  );
  return (
    <EvalRunDetailSection
      title="실행 요약"
      actions={<EpisodeTimesButton runId={runId} termsState={termsState} onRetry={onRetry} />}
    >
      {failuresAfterReset !== null && failuresAfterReset > 0 ? (
        <EvalRunDetailNotice tone="warning">
          auto-reset 직후 실패가 {failuresAfterReset}건 발생했습니다. 해당 Episode의 종료 시간과
          원인을 정확히 확인할 수 없습니다.
        </EvalRunDetailNotice>
      ) : null}
      {/* 받지 못한 조회의 차트는 그리지 않는다. 그 자리가 비어 보이지 않도록 대기와 실패를 알린다. */}
      {pending ? <LoadingState size="sm">실행 결과를 불러오는 중입니다…</LoadingState> : null}
      {failure !== null ? (
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
          {failure}
        </EmptyState>
      ) : null}
      {p95 !== null ? (
        <div className={styles.statGrid}>
          <EvalRunDetailSummaryStat label="p95 지연 시간" value={`${formatDetailNumber(p95)} ms`} />
        </div>
      ) : null}
      {baseline.status === "loading" ? (
        <LoadingState size="sm">Baseline을 불러오는 중입니다.</LoadingState>
      ) : null}
      {baseline.notices.map((notice) => (
        <EvalRunDetailNotice
          key={notice.key}
          tone={notice.tone === "warning" ? "warning" : "neutral"}
        >
          {notice.message}
        </EvalRunDetailNotice>
      ))}
      {baselineSeries !== null && successRate !== null ? (
        <div className={styles.chartCard}>
          <KpiCompareChart
            items={[
              baselineSeries,
              {
                ...DETAIL_RUN_SERIES,
                summary: { ...DETAIL_RUN_SERIES.summary, finalSuccessRate: successRate },
              },
            ]}
          />
        </div>
      ) : null}
      {outcomes.length > 0 ? (
        <OutcomeChart
          outcomes={outcomes}
          total={terms?.episodes.length ?? 0}
          baseline={baselineSeries}
        />
      ) : null}
      {terms !== null && terms.terms.length > 0 ? (
        <TermDistributions terms={terms.terms} baseline={baselineSeries} />
      ) : null}
    </EvalRunDetailSection>
  );
}

// 지표와 success term 은 따로 받는다. 실패한 쪽의 이름을 문장에 적는다. 둘 다 실패해도 안내는 한 줄이다.
function resultLoadFailure(
  metricsState: EvalRunDetailController["metricsState"],
  termsState: EvalRunDetailController["termsState"],
): string | null {
  if (metricsState.status === "error") {
    const target = termsState.status === "error" ? "지표와 success term 을" : "지표를";
    return `${target} 불러오지 못했습니다: ${metricsState.message.slice(0, 180)}`;
  }
  if (termsState.status === "error") {
    return `Success term 을 불러오지 못했습니다: ${termsState.message.slice(0, 180)}`;
  }
  return null;
}

type OutcomeBar = { key: string; count: number; total: number; color: string; source: string };

// 막대 높이는 각 계열의 전체 대비 비율이다. Seed 를 합친 baseline 은 Episode 수가 훨씬 많다.
// 개수로 높이를 정하면 "결과가 더 많다"가 "성적이 다르다"로 읽힌다. 계열이 하나면 개수로 정할
// 때와 높이가 같다.
function outcomeRows(
  outcomes: ReturnType<typeof outcomeBreakdown>,
  total: number,
  baseline: BaselineSeries | null,
): { label: string; bars: OutcomeBar[] }[] {
  const labels = outcomes.map((item) => item.label);
  const baselineTotal = baseline?.baseline.episodeCount ?? 0;
  for (const item of baseline?.summary.outcomeBreakdown ?? [])
    if (!labels.includes(item.label)) labels.push(item.label);
  return labels.map((label) => {
    const own = outcomes.find((item) => item.label === label);
    const baselineItem = baseline?.summary.outcomeBreakdown.find((item) => item.label === label);
    const runBar: OutcomeBar = {
      key: "run",
      count: own?.count ?? 0,
      total,
      color: own?.color ?? "var(--c6-color-border-subtle)",
      source: "이 실행",
    };
    if (baseline === null) return { label, bars: [runBar] };
    // baseline 은 비교 창과 같이 왼쪽에 표시한다.
    const baselineBar: OutcomeBar = {
      key: "baseline",
      count: baselineItem?.count ?? 0,
      total: baselineTotal,
      color: baseline.color,
      source: "Baseline",
    };
    return { label, bars: [baselineBar, runBar] };
  });
}

function OutcomeChart({
  outcomes,
  total,
  baseline,
}: {
  outcomes: ReturnType<typeof outcomeBreakdown>;
  total: number;
  baseline: BaselineSeries | null;
}): ReactNode {
  const rows = outcomeRows(outcomes, total, baseline);
  const share = (bar: OutcomeBar) => (bar.total > 0 ? bar.count / bar.total : 0);
  const maximum = Math.max(...rows.flatMap((row) => row.bars.map(share)), Number.MIN_VALUE);
  const width = 640;
  const height = 180;
  const plotTop = 18;
  const plotBottom = 145;
  const slot = (width - 48) / rows.length;
  return (
    <div className={styles.chartCard}>
      <span className={styles.muted}>Episode 결과 (총 {total}개)</span>
      <svg
        className={styles.outcomeChart}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Episode 결과, 총 ${total}개`}
      >
        <line x1="36" x2={width - 12} y1={plotBottom} y2={plotBottom} className={styles.axis} />
        {rows.map((row, index) => {
          const groupX = 36 + index * slot + slot * 0.2;
          const groupWidth = slot * 0.6;
          const barWidth = groupWidth / row.bars.length;
          return (
            <g key={`${row.label}:${index}`}>
              {row.bars.map((bar, barIndex) => {
                const barHeight = ((plotBottom - plotTop) * share(bar)) / maximum;
                const x = groupX + barIndex * barWidth;
                return (
                  <g key={bar.key}>
                    <title>{`${bar.source} ${row.label}: ${bar.count}/${bar.total}`}</title>
                    <rect
                      x={x}
                      y={plotBottom - barHeight}
                      width={barWidth * (row.bars.length > 1 ? 0.88 : 1)}
                      height={barHeight}
                      rx="4"
                      fill={bar.color}
                    />
                    <text x={x + barWidth / 2} y={plotBottom - barHeight - 5} textAnchor="middle">
                      {bar.count}
                    </text>
                  </g>
                );
              })}
              <text x={groupX + groupWidth / 2} y={plotBottom + 17} textAnchor="middle">
                {row.label}
              </text>
            </g>
          );
        })}
      </svg>
      {baseline !== null ? (
        <span className={styles.muted}>
          왼쪽 막대가 Baseline입니다. Episode 수가 서로 달라 막대 높이는 전체 대비 비율로 그렸고,
          숫자는 실제 Episode 수입니다.
        </span>
      ) : null}
    </div>
  );
}

function TermDistributions({
  terms,
  baseline,
}: {
  terms: readonly TrainingEvalTermView[];
  baseline: BaselineSeries | null;
}): ReactNode {
  const { scalar, unsupported } = useMemo(() => {
    const baselineStats = new Map(
      (baseline?.summary.distributions ?? []).map((dist) => [dist.key, dist.stats]),
    );
    const scalar: Array<{
      term: TrainingEvalTermView;
      stats: BoxStats;
      baselineStats: BoxStats | null;
    }> = [];
    const unsupported: TrainingEvalTermView[] = [];
    for (const term of terms) {
      const stats = termBoxStats(term);
      if (stats === null) unsupported.push(term);
      else scalar.push({ term, stats, baselineStats: baselineStats.get(term.name) ?? null });
    }
    return { scalar, unsupported };
  }, [terms, baseline]);
  if (scalar.length === 0) {
    return (
      <div className={styles.distributions}>
        <EmptyState size="sm">분포를 그릴 scalar term 이 없습니다.</EmptyState>
        <UnsupportedTermsSummary terms={unsupported} />
      </div>
    );
  }
  return (
    <div className={styles.distributions}>
      <div className={styles.distributionHeader}>
        <span>분포</span>
        {unsupported.length > 0 ? (
          <span className={styles.muted}>vector·미지원 term {unsupported.length}개 숨김</span>
        ) : null}
      </div>
      <div className={boxPlotGridClass}>
        {scalar.map((entry) => (
          <TermBoxPlot
            key={entry.term.name}
            term={entry.term}
            stats={entry.stats}
            baselineStats={entry.baselineStats}
            baselineColor={baseline?.color ?? null}
          />
        ))}
      </div>
      <UnsupportedTermsSummary terms={unsupported} />
    </div>
  );
}

function UnsupportedTermsSummary({ terms }: { terms: readonly TrainingEvalTermView[] }): ReactNode {
  if (terms.length === 0) return null;
  return (
    <div className={styles.unsupportedTerms} data-testid="success-term-unsupported">
      {terms.slice(0, 3).map((term) => (
        <div key={term.name}>
          <span>{term.name}</span> 은 v1 chart 가 지원하지 않는 term 입니다. vector·list term 은
          원시 미리보기만 제공합니다.
          {term.raw_preview !== null ? (
            <pre>{JSON.stringify(term.raw_preview, null, 2)}</pre>
          ) : null}
        </div>
      ))}
      {terms.length > 3 ? <div>term {terms.length - 3}개 더 있음(생략)</div> : null}
    </div>
  );
}

function TermBoxPlot({
  term,
  stats,
  baselineStats,
  baselineColor,
}: {
  term: TrainingEvalTermView;
  stats: BoxStats;
  baselineStats: BoxStats | null;
  baselineColor: string | null;
}): ReactNode {
  // 시리즈가 하나면 label·color 없이 기존 teal 을 유지한다. baseline 이 있을 때만 두 그룹을 표시한다.
  const groups =
    baselineStats === null
      ? [{ key: term.name, stats }]
      : [
          {
            key: `${term.name}:baseline`,
            label: "Baseline",
            stats: baselineStats,
            color: baselineColor ?? undefined,
          },
          { key: term.name, label: "이 실행", stats },
        ];
  return (
    <BoxPlotChart title={term.name} groups={groups}>
      <div className={styles.termExtras}>
        <span className={styles.muted}>
          med {formatDetailNumber(stats.median)} · n={term.count}
        </span>
        {Object.keys(term.outcome_counts).length > 0 ? (
          <div className={styles.outcomeCounts}>
            {Object.entries(term.outcome_counts).map(([outcome, count]) => (
              <span key={outcome}>
                {outcome}: {count}
              </span>
            ))}
          </div>
        ) : null}
        {term.warning !== null ? <span className={styles.warningText}>{term.warning}</span> : null}
      </div>
    </BoxPlotChart>
  );
}
